import { supabase } from './supabase';
import { User } from '@supabase/supabase-js';

interface ErrorLog {
  error_message: string;
  error_stack?: string;
  component?: string;
  user_id?: string;
  additional_context?: Record<string, unknown>;
  browser_info?: string;
  url?: string;
}

// Cache auth state to reduce API calls
let cachedUser: User | null = null;
let cacheTimestamp = 0;
const CACHE_DURATION = 60000; // 1 minute cache duration

// Function to get cached user, refreshing only when needed
const getCachedUser = async () => {
  const now = Date.now();
  if (cachedUser && now - cacheTimestamp < CACHE_DURATION) {
    return cachedUser;
  }
  
  try {
    const { data } = await supabase.auth.getUser();
    cachedUser = data.user;
    cacheTimestamp = now;
    return cachedUser;
  } catch (error) {
    console.error('Error getting cached user:', error);
    return null;
  }
};

// Function to sanitize error messages and stacks
const sanitizeErrorInfo = (text: string | undefined): string | undefined => {
  if (!text) return text;
  
  // Replace Supabase URLs with a generic placeholder
  const sanitized = text
    .replace(/https?:\/\/[\w-]+\.supabase\.co\/(rest|storage|auth)\/[\w/-]+/g, '[DATABASE-URL]')
    .replace(/idsakcilnnqhliybglsr\.supabase\.co/g, '[DATABASE-HOST]')
    .replace(/ERR_INTERNET_DISCONNECTED/g, 'INTERNET_DISCONNECTED');
    
  return sanitized;
};

export const logError = async (
  error: Error | unknown, 
  component?: string, 
  additionalContext?: Record<string, unknown>
) => {
  try {
    // First check if we have an active session to avoid unnecessary API calls
    let hasActiveSession = false;
    try {
      const { data } = await supabase.auth.getSession();
      hasActiveSession = !!data.session;
    } catch (sessionError) {
      // If we can't check the session, assume we're not authenticated
      hasActiveSession = false;
    }
    
    // If no active session, don't attempt to log to Supabase
    if (!hasActiveSession) {
      console.warn('Error occurred while not authenticated:', 
        error instanceof Error ? `${error.name}: ${error.message}` : String(error));
      return;
    }
    
    // Now get cached user since we confirmed we have a session
    const user = await getCachedUser();
    
    if (!user) {
      // Don't attempt to log if we couldn't get user info
      console.warn('Error occurred with no user info:', error);
      return;
    }

    // Extract and sanitize the error details more safely
    let errorMessage = 'Unknown error';
    let errorStack: string | undefined;
    
    if (error instanceof Error) {
      errorMessage = error.message;
      errorStack = error.stack;
    } else if (error !== null && typeof error === 'object') {
      // Try to extract message from object-like errors
      const errorObj = error as Record<string, any>;
      errorMessage = 
        typeof errorObj.message === 'string' ? errorObj.message : 
        typeof errorObj.error === 'string' ? errorObj.error : 
        JSON.stringify(error);
        
      // Try to extract stack if available
      errorStack = typeof errorObj.stack === 'string' ? errorObj.stack : undefined;
    } else {
      // For primitive values
      errorMessage = String(error);
    }
    
    // Sanitize the error message and stack
    errorMessage = sanitizeErrorInfo(errorMessage) || 'Unknown error';
    errorStack = sanitizeErrorInfo(errorStack);
    
    // Skip logging certain network errors completely
    if (
      errorMessage.includes('INTERNET_DISCONNECTED') || 
      errorMessage.includes('Failed to fetch') ||
      errorMessage.includes('Auth session missing') ||
      errorMessage.includes('JWT expired')
    ) {
      console.warn('Network or auth error occurred, skipping server logging:', errorMessage);
      return;
    }
    
    const errorLog: ErrorLog = {
      error_message: errorMessage,
      error_stack: errorStack,
      component,
      user_id: user?.id,
      additional_context: additionalContext,
      browser_info: navigator.userAgent,
      url: window.location.href,
    };

    // Use a timeout to prevent blocking the UI thread
    const timeoutPromise = new Promise<void>((resolve) => {
      setTimeout(() => {
        resolve();
      }, 3000); // 3 second timeout
    });

    // Race between error logging and timeout
    await Promise.race([
      supabase.from('error_logs').insert([errorLog]).then(({error: insertError}) => {
        if (insertError) {
          console.error('Failed to log error to Supabase:', insertError);
          console.error('Original error:', errorMessage);
        }
      }),
      timeoutPromise.then(() => {
        console.warn('Error logging timed out after 3 seconds');
      })
    ]);
  } catch (loggingError) {
    // Last resort fallback if everything fails
    console.error('Error during error logging:', loggingError);
    console.error('Original error:', error instanceof Error ? error.message : String(error));
  }
}; 